import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // true no início — checando sessão
  const [error, setError] = useState(null);

  // Tenta restaurar sessão via /me ao montar
  const restoreSession = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user || data);
      setError(null);
    } catch {
      // Sem sessão válida — usuário é visitante
      setUser(null);
      setError(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    restoreSession();

    // Escuta evento de sessão expirada (do interceptor do axios)
    const onExpired = () => {
      setUser(null);
      setError('Sessão expirada. Faça login novamente.');
    };
    window.addEventListener('nc:session-expired', onExpired);
    return () => window.removeEventListener('nc:session-expired', onExpired);
  }, [restoreSession]);

  const login = useCallback(async (email, password) => {
    setError(null);
    const { data } = await api.post('/auth/login', { email, password });
    // Após login, busca dados do usuário
    const { data: meData } = await api.get('/auth/me');
    setUser(meData.user || meData);
    return meData.user || meData;
  }, []);

  const register = useCallback(async ({ name, email, password, phone }) => {
    setError(null);
    const { data } = await api.post('/auth/register', { name, email, password, phone });
    const { data: meData } = await api.get('/auth/me');
    setUser(meData.user || meData);
    return meData.user || meData;
  }, []);

  // LE-034: login com Google — envia a credential (ID token) do GIS; o backend
  // verifica assinatura/audiência/expiração server-side e cria/vincula a conta.
  const loginGoogle = useCallback(async (credential) => {
    setError(null);
    const { data } = await api.post('/auth/google', { credential });
    const { data: meData } = await api.get('/auth/me');
    setUser(meData.user || meData);
    return meData.user || meData;
  }, []);

  // LE-040: primeiro acesso com convite — valida o código + e-mail e devolve os
  // dados a confirmar (nome/telefone pré-preenchidos pelo admin, quando houver).
  // NÃO consome o convite; falhas de validação contam como tentativa (fail-closed).
  const inviteInfo = useCallback(async ({ code, email }) => {
    setError(null);
    const { data } = await api.post('/auth/invite/info', { code, email });
    return data;
  }, []);

  // LE-040: resgata o convite — confirma os dados, cria a senha e já autentica.
  const redeemInvite = useCallback(async ({ code, email, name, phone, password }) => {
    setError(null);
    await api.post('/auth/invite/redeem', { code, email, name, phone, password });
    const { data: meData } = await api.get('/auth/me');
    setUser(meData.user || meData);
    return meData.user || meData;
  }, []);

  const logout = useCallback(async () => {
    try {
      // Busca CSRF token e faz logout
      const { data: csrfData } = await api.get('/auth/csrf');
      await api.post('/auth/logout', {}, {
        headers: { 'X-CSRF-Token': csrfData.csrfToken },
      });
    } catch {
      // Mesmo se falhar, limpa localmente
    }
    setUser(null);
  }, []);

  // LGPD: exporta dados pessoais (art. 18)
  const exportData = useCallback(async () => {
    const { data } = await api.get('/auth/me/export');
    return data;
  }, []);

  // LGPD: exclui/anonimiza a conta (art. 18, VI)
  const deleteAccount = useCallback(async () => {
    const { data: csrfData } = await api.get('/auth/csrf');
    await api.delete('/auth/me', {
      headers: { 'X-CSRF-Token': csrfData.csrfToken },
    });
    setUser(null);
    return data;
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user || data);
    } catch {
      // ignora — sessão pode ter expirado
    }
  }, []);

  // LE-023/LE-027: atualiza dados do perfil (PATCH /auth/me, CSRF via
  // interceptor). Aceita { avatar, name, phone } — só os campos enviados são
  // alterados no banco (UPDATE dinâmico no backend).
  const updateProfile = useCallback(async (fields) => {
    const { data } = await api.patch('/auth/me', fields);
    setUser(prev => ({ ...(prev || {}), ...data.user }));
    return data.user;
  }, []);

  // LE-023: atalho para atualizar apenas o avatar (usado pela UI de upload)
  const updateAvatar = useCallback((avatar) => updateProfile({ avatar }), [updateProfile]);

  // LE-029: troca de senha (POST /auth/change-password, CSRF via interceptor).
  // O backend verifica a senha atual (prova de posse), proíbe reuso e revoga as
  // outras sessões; a sessão atual permanece válida.
  const changePassword = useCallback(async ({ currentPassword, newPassword }) => {
    const { data } = await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return data;
  }, []);

  const isVisitor = !user;
  const isMerchant = user?.role === 'merchant';
  const isAdmin = user?.role === 'admin';
  const isDelivery = user?.role === 'delivery';

  return (
    <AuthContext.Provider value={{
      user,
      login,
      register,
      loginGoogle,
      inviteInfo,
      redeemInvite,
      logout,
      exportData,
      deleteAccount,
      refreshUser,
      updateProfile,
      updateAvatar,
      changePassword,
      loading,
      error,
      isVisitor,
      isMerchant,
      isAdmin,
      isDelivery,
      restoreSession,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
